import React, { useState } from "react";
import { Send, Clock, CheckCircle, Trash2, CheckCheck } from "lucide-react";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Loader2,
  FileText,
  Shield,
  Receipt,
  CreditCard,
  BarChart3,
  HelpCircle,
  Zap,
  Inbox,
} from "lucide-react";

import { format } from "date-fns";
import { fr } from "date-fns/locale"; // <-- add this import
import { httpClient } from '../../../HttpClient';

import { dialog } from "../../../components/ui/Dialog";
const typeConfig = {
  kbis: { label: "Extrait K-bis", icon: FileText },
  attestation_vigilance: { label: "Attestation de vigilance", icon: Shield },
  attestation_fiscale: { label: "Attestation fiscale", icon: Receipt },
  RIB: { label: "RIB", icon: CreditCard },
  situation_comptable: { label: "Situation comptable", icon: BarChart3 },
  autre: { label: "Autre demande", icon: HelpCircle },
};

// Clés = valeurs réelles de `etat` (voir demandeController.js et les
// filtres enCours/terminees ci-dessous) — pas "pending"/"in_progress"/
// "completed", qui ne correspondent à aucune valeur jamais envoyée par le
// backend (bug : le badge retombait donc toujours sur "En attente").
const statusConfig = {
  EN_ATTENTE: {
    label: "En attente",
    color: "bg-slate-100 text-slate-600",
    icon: Clock,
  },
  "En cours": {
    label: "En cours",
    color: "bg-blue-100 text-blue-700",
    icon: Loader2,
  },
  Terminés: {
    label: "Terminée",
    color: "bg-green-100 text-green-700",
    icon: CheckCircle,
  },
};

function Demandes({ demandes }) {
  const [activeTab, setActiveTab] = useState("all");
  const [loadingAction, setLoadingAction] = useState({
    validate: null, // id en cours de validation
    delete: null, // id en cours de suppression
  });

  // 📌 Filtres
  const enCours = demandes.filter(
    (d) => d.etat === "En cours" || d.etat === "EN_ATTENTE"
  );
  const terminees = demandes.filter((d) => d.etat === "Terminés");

  const getDemandesByTab = () => {
    if (activeTab === "en_cours") return enCours;
    if (activeTab === "terminees") return terminees;
    return demandes;
  };

  const renderTypeLabel = (type) => {
    switch (type) {
      case "kbis":
        return "Extrait K-bis";
      case "attestation_vigilance":
        return "Attestation de vigilance";
      case "attestation_fiscale":
        return "Attestation fiscale";
      case "rib":
        return "RIB";
      case "situation_comptable":
        return "Situation comptable";
      case "autre":
        return "Autre demande";
      default:
        return type;
    }
  };

  const handleDelete = async (id) => {
    const confirmDelete = await dialog.confirm(
      "Voulez-vous vraiment supprimer cette demande ?"
    );
    if (!confirmDelete) return;

    setLoadingAction((prev) => ({ ...prev, delete: id }));

    try {
      await httpClient.delete(`https://backend-myalfa.vercel.app/api/demande/${id}`);
    } catch (error) {
      console.error(error);
      dialog.alert("Erreur lors de la suppression");
    } finally {
      setLoadingAction((prev) => ({ ...prev, delete: null }));
    }
  };

  const handleValidate = async (demande) => {
    const confirmValidate = await dialog.confirm(
      "Valider cette demande ? Un email sera envoyé au client."
    );
    if (!confirmValidate) return;

    setLoadingAction((prev) => ({ ...prev, validate: demande.id }));

    try {
      await httpClient.put(
        `https://backend-myalfa.vercel.app/api/demande/${demande.id}/etat`,
        {
          type: demande.type,
          details: demande.details,
          email: "rantsajonathan@gmail.com",
          client: "Test Client",
        }
      );
    } catch (error) {
      console.error(error);
      dialog.alert("Erreur lors de la validation");
    } finally {
      setLoadingAction((prev) => ({ ...prev, validate: null }));
    }
  };

  return (
    <div className="min-h-screen">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-2xl shadow-sm p-4 mb-6">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="bg-slate-100 p-1">
              <TabsTrigger
                value="all"
                className="data-[state=active]:bg-white gap-2"
              >
                <Send className="w-4 h-4" />
                Toutes ({demandes.length})
              </TabsTrigger>

              <TabsTrigger
                value="en_cours"
                className="data-[state=active]:bg-white gap-2"
              >
                <Clock className="w-4 h-4" />
                En cours ({enCours.length})
              </TabsTrigger>

              <TabsTrigger
                value="terminees"
                className="data-[state=active]:bg-white gap-2"
              >
                <CheckCircle className="w-4 h-4" />
                Terminées ({terminees.length})
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        <Section
          demandes={getDemandesByTab()}
          renderTypeLabel={renderTypeLabel}
          handleDelete={handleDelete}
          handleValidate={handleValidate}
          loadingAction={loadingAction}
        />
      </div>
    </div>
  );
}

// 🧱 Liste des demandes
function Section({
  demandes,
  renderTypeLabel,
  handleDelete,
  handleValidate,
  loadingAction,
}) {
  if (demandes.length === 0) {
    return (
      <div className="text-center py-16">
        <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Inbox className="w-8 h-8 text-slate-400" />
        </div>
        <h3 className="text-lg font-medium text-slate-800">Aucune demande</h3>
        <p className="text-slate-500 mt-1">Aucune demande pour l'instant</p>
      </div>
    );
  }

  return (
    <ul className="space-y-4">
      {demandes.map((d) => {
        const type = typeConfig[d.type] || typeConfig.autre;
        const status = statusConfig[d.etat] || statusConfig.EN_ATTENTE;
        const Icon = type.icon;
        const StatusIcon = status.icon;

        return (
          <li
            key={d.id}
            className="bg-white rounded-2xl shadow-sm p-4 hover:shadow-md transition-all duration-300 border-slate-200 border-2"
          >
            <div className="flex items-start gap-4 justify-between">
              {/* Icône type */}
              <div className="p-3 rounded-xl bg-slate-100 flex-shrink-0 flex items-center justify-center">
                <Icon className="w-5 h-5 text-slate-600" />
              </div>

              <div className="flex-1 min-w-0">
                {/* Header avec type et urgence */}
                <div className="flex items-center gap-2 flex-wrap">
                  <h4 className="font-medium text-slate-800">
                    {renderTypeLabel(d.type)}
                  </h4>
                  {d.urgence.toLowerCase() === "urgent" && (
                    <div className="flex items-center gap-1 text-amber-700 bg-amber-100 text-xs px-2 py-1 rounded-full font-medium">
                      <Zap className="w-3 h-3" />
                      Urgent
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-2 mt-2">
                  <div
                    variant="secondary"
                    className={`${status.color} flex items-center gap-1 text-amber-700 bg-amber-100 text-xs px-2 py-1 rounded-full font-medium`}
                  >
                    <StatusIcon
                      className={`w-3 h-3 ${
                        status.icon === Loader2 ? "animate-spin" : ""
                      }`}
                    />
                    {status.label}
                  </div>
                  <span className="text-xs text-slate-400">
                    {format(new Date(d.createdAt), "d MMM yyyy", {
                      locale: fr,
                    })}
                  </span>
                </div>

                {/* Détails */}
                {d.details && (
                  <p className="text-sm text-slate-500 mt-2">{d.details}</p>
                )}
              </div>

              <div className="flex flex-col gap-2 ml-2">
                {/* ✅ Valider */}
                {d.etat !== "Terminés" && (
                  <button
                    onClick={() => handleValidate(d)}
                    className="p-2 rounded-xl bg-green-100 hover:bg-green-200 transition gap-2 flex items-center justify-between"
                  >
                    <CheckCheck className="w-4 h-4 text-green-600" />
                    <p className="text-xs text-green-500">
                      {loadingAction.validate === d.id
                        ? "Validation..."
                        : "Valider"}
                    </p>
                  </button>
                )}

                {/* 🗑 Supprimer */}
                <button
                  onClick={() => handleDelete(d.id)}
                  className="p-2 rounded-xl bg-red-100 hover:bg-red-200 transition gap-2 flex items-center justify-between"
                >
                  <Trash2 className="w-4 h-4 text-red-600" />
                  <p className="text-xs text-red-500">
                    {loadingAction.delete === d.id
                      ? "Suppression..."
                      : "Suprm."}
                  </p>
                </button>
              </div>
            </div>
          </li>
        );
      })}
    </ul>
  );
}
export default Demandes;
